import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  BarChart2,
  BarChart3,
  ChevronDown,
  Pause,
  Play,
  StepBack,
  StepForward,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useIsPhone } from '@/hooks/useIsPhone'
// Relative imports (not the '@/components' barrel) to avoid a cycle now that the
// barrel exports Workbench, which renders this file.
import { Popover, PopoverContent, PopoverTrigger } from '../Popover/Popover'
import { Grip, ToolbarButton, type DragOffset } from './WorkbenchFloatingBits'
import { paceLabel, pacesFor, type ReplayDetail } from './walkthroughPacing'

const NO_DRAG: DragOffset = { dx: 0, dy: 0 }

/**
 * The two granularity rows, shared by the desktop pill and the phone band so the pair can
 * never name the same detail with two different glyphs. There is no third row: real time is
 * not a granularity, it is tick at 1x, and it lives in the pace list where a speed belongs.
 *
 * Tick is `BarChart3` (bars stepping forward) rather than an activity zigzag: the detail it
 * names is "more bars, finer", not "a squiggle".
 */
const DETAIL_ROWS: ReadonlyArray<{
  key: ReplayDetail
  label: string
  hint: string
  Icon: typeof BarChart2
}> = [
  { key: 'candle', label: 'Candle', hint: 'Bar by bar', Icon: BarChart2 },
  { key: 'tick', label: 'Tick', hint: 'Finer steps', Icon: BarChart3 },
]

export interface WorkbenchReplayBarProps {
  playing: boolean
  /**
   * Pace within the current detail. A feel under Candle; a literal multiple of the market
   * clock under Tick, where 1x IS real time.
   */
  pace: number
  onPaceChange: (pace: number) => void
  /** Candle | Tick. Decides the pace list and what a pace MEANS. */
  detail: ReplayDetail
  onDetailChange: (detail: ReplayDetail) => void
  /** Index space, from useScrubProjection. */
  value: number
  max: number
  /** Cap so the user can never aim past data the run has actually produced. */
  maxScrubIndex?: number
  onScrub: (index: number) => void
  onTogglePlay: () => void
  onStepBack: () => void
  onStepForward: () => void
  /**
   * User-chosen offset from the bar's anchored position, and the setter behind its grip.
   *
   * The bar floats over the chart, so wherever it is anchored it will sometimes cover
   * something the user wants to see. Owned by the parent (useFloatingBarDrag) rather
   * than here: the position has to survive this component unmounting, which it does on
   * every walkthrough rebuild.
   *
   * Desktop only — the phone band is a row of the chart column and is pinned.
   */
  drag?: DragOffset
  onDragChange?: (d: DragOffset) => void
  /** Double-clicking the grip re-centres. */
  onDragReset?: () => void
  /** Attach point for the parent's clamp — the element the offset moves. */
  barRef?: (el: HTMLElement | null) => void
  className?: string
}

/**
 * Desktop-only icon button.
 *
 * The shared `ToolbarButton` is 32px tall and built for a toolbar row; this bar is 36px
 * total, so its controls are 24px. The phone band DOES use the shared primitive — at that
 * size a thumb target is the requirement and the shared 36px square is exactly right.
 */
function IconButton({
  onClick,
  title,
  label,
  disabled,
  children,
}: {
  onClick: () => void
  /** Tooltip text. May explain WHY the control is off, which the name must not. */
  title: string
  /** Accessible name. Defaults to `title`. */
  label?: string
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      // `title` is a hover affordance and never fires on touch, so every control in this bar
      // also carries its name for assistive tech. The name is the control's IDENTITY and
      // stays put when the tooltip switches to a reason — two controls sharing one name
      // because they are off for the same reason is not a way to identify either of them.
      aria-label={label ?? title}
      disabled={disabled}
      className="inline-flex items-center justify-center h-6 w-6 rounded-md text-muted-foreground
                 hover:bg-muted/60 hover:text-foreground transition-colors cursor-pointer
                 disabled:opacity-40 disabled:pointer-events-none"
    >
      {children}
    </button>
  )
}

/**
 * The walkthrough's playback transport.
 *
 * Two layouts, ONE prop list and ONE pair of dropdowns. Desktop keeps it floating and
 * draggable inside the chart pane, docked at the bottom where a player belongs; a phone
 * renders it as its own full-width row between the chart and the trades pane, where its top
 * edge doubles as the progress bar. A second hand-copied control set is exactly how the two
 * would quietly drift apart in spacing, motion or dismissal.
 *
 * Kept separate from ChartReplayBar rather than made a variant of it. That component is a
 * docked, full-width bottom bar with its own hover-scrub track and two existing consumers;
 * this one floats, carries per-bar step controls and a detail selector the Backtest tab has
 * no use for. What they DO share is the index↔time projection (useScrubProjection).
 *
 * There is no Restart: the scrubber's own zero is the way back to the top. Nor is there an
 * Exit — leaving the mode is the toolbar's job, in the same spot that entered it. This bar is
 * a transport and nothing else.
 */
export function WorkbenchReplayBar({
  playing,
  pace,
  onPaceChange,
  detail,
  onDetailChange,
  value,
  max,
  maxScrubIndex,
  onScrub,
  onTogglePlay,
  onStepBack,
  onStepForward,
  drag = NO_DRAG,
  onDragChange,
  onDragReset,
  barRef,
  className,
}: WorkbenchReplayBarProps) {
  const [paceOpen, setPaceOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const isPhone = useIsPhone()
  const bandRef = useRef<HTMLDivElement | null>(null)

  const paces = pacesFor(detail)
  const scrubMax = Math.max(0, max)
  const pct = scrubMax > 0 ? Math.min(100, Math.max(0, (value / scrubMax) * 100)) : 0
  const detailLabel = detail === 'tick' ? 'Tick' : 'Candle'
  const DetailIcon = detail === 'tick' ? BarChart3 : BarChart2

  /**
   * Tap anywhere outside to close either dropdown.
   *
   * The popover primitive already dismisses on an outside press — but it listens on the
   * document in the BUBBLE phase, and the chart pane stops propagation on its own pointer
   * handlers, so a press on the chart never reaches it. That is the one surface a trader is
   * most likely to press next. Listening in the CAPTURE phase gets there first, so the
   * chart's own gesture handling is irrelevant. Deliberately NOT solved by removing the
   * chart's stopPropagation — that guard exists so a press meant for the chart cannot also
   * drive the surfaces above it.
   */
  useEffect(() => {
    if (!paceOpen && !detailOpen) return
    const onDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null
      // Inside the bar itself — the triggers live here, and the primitive's own toggle
      // handles them.
      if (target && bandRef.current?.contains(target)) return
      // Inside either open list. Both are portalled out of the bar, so the check above
      // cannot catch them.
      if (target?.closest?.('[data-radix-popper-content-wrapper]')) return
      setPaceOpen(false)
      setDetailOpen(false)
    }
    document.addEventListener('pointerdown', onDown, true)
    return () => document.removeEventListener('pointerdown', onDown, true)
  }, [paceOpen, detailOpen])

  const scrubTo = (raw: number) => {
    onScrub(maxScrubIndex == null ? raw : Math.min(raw, maxScrubIndex))
  }

  const handleDetail = (next: ReplayDetail) => {
    onDetailChange(next)
    setDetailOpen(false)
  }

  // ── The two dropdowns, written once and worn by both layouts ────────────────────────────
  // Pace and Detail are the only controls here that pick one value from a short list, they
  // sit side by side, and they both open UPWARD off a bar near the bottom of the chart.
  const paceControl = (phone: boolean) => (
    <Popover open={paceOpen} onOpenChange={setPaceOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Playback speed"
          aria-label="Playback speed"
          className={cn(
            'inline-flex items-center gap-0.5 rounded-md text-caption font-semibold tabular-nums',
            phone ? 'h-9 px-2 select-none' : 'h-6 px-1.5',
            'hover:bg-muted/60 hover:text-foreground transition-colors cursor-pointer',
            paceOpen ? 'text-foreground bg-muted/60' : 'text-muted-foreground',
          )}
        >
          {paceLabel(detail, pace)}
          <ChevronDown
            size={10}
            strokeWidth={2}
            className={cn('opacity-50 transition-transform', paceOpen && 'rotate-180')}
          />
        </button>
      </PopoverTrigger>
      {/* Viewport-safe: both popovers open off a bar that runs to within 8px of a phone's
          edges, so they get an explicit collision gutter rather than being allowed to sit
          flush against — or past — the screen edge. */}
      {/* Wide enough for "Real-time" in tick detail; candle's three numbers need a sliver. */}
      <PopoverContent
        align="center"
        side="top"
        collisionPadding={8}
        className={cn('p-1', detail === 'tick' ? 'w-28' : 'w-16')}
      >
        {paces.map((s) => {
          const active = s === pace
          const label = paceLabel(detail, s)
          return (
            <button
              key={s}
              type="button"
              title={
                detail === 'tick'
                  ? s === 1
                    ? "Play at the market's own pace"
                    : `Play at ${s} times real time`
                  : `Play at ${label}`
              }
              onClick={() => {
                onPaceChange(s)
                setPaceOpen(false)
              }}
              className={cn(
                'w-full flex items-center px-2 rounded text-caption tabular-nums cursor-pointer transition-colors',
                phone ? 'min-h-11' : 'py-1',
                active
                  ? 'bg-muted text-foreground font-medium'
                  : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
              )}
            >
              {label}
            </button>
          )
        })}
      </PopoverContent>
    </Popover>
  )

  const detailControl = (phone: boolean) => (
    <Popover open={detailOpen} onOpenChange={setDetailOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Playback detail"
          aria-label="Playback detail"
          className={cn(
            'inline-flex items-center gap-0.5 rounded-md text-caption font-semibold',
            phone ? 'h-9 px-2 select-none' : 'h-6 px-1.5',
            'hover:bg-muted/60 hover:text-foreground transition-colors cursor-pointer',
            detailOpen ? 'text-foreground bg-muted/60' : 'text-muted-foreground',
          )}
        >
          {/* The leading icon is phone-only. On a phone this control is the one thing on the
              row still carrying a word, so it earns icon + word + chevron — and the icon is
              the ACTIVE detail's own, so the closed control says which detail you are in
              twice over rather than just naming a menu. Desktop has the room for the word
              alone. */}
          {phone && <DetailIcon size={12} strokeWidth={2} className="shrink-0" />}
          {detailLabel}
          <ChevronDown
            size={10}
            strokeWidth={2}
            className={cn('opacity-50 transition-transform', detailOpen && 'rotate-180')}
          />
        </button>
      </PopoverTrigger>
      {/* Wide enough that a label and its hint cannot break mid-word, and still inside a
          375px phone. Every row carries an icon in the same slot; the active row is marked by
          highlight plus icon colour. */}
      <PopoverContent align="center" side="top" collisionPadding={8} className="w-44 p-1">
        <div className="px-2 pt-1 pb-0.5 text-caption text-muted-foreground">Detail</div>
        {DETAIL_ROWS.map((g) => {
          const active = detail === g.key
          return (
            <button
              key={g.key}
              type="button"
              title={g.key === 'candle' ? 'Play candle by candle' : 'Play tick by tick'}
              onClick={() => handleDetail(g.key)}
              className={cn(
                'w-full flex items-center gap-2 px-2 rounded cursor-pointer transition-colors',
                phone ? 'min-h-11' : 'py-1',
                active
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
              )}
            >
              <g.Icon
                size={12}
                strokeWidth={2}
                className={cn('shrink-0', active ? 'text-primary' : 'opacity-50')}
              />
              <span className="text-caption font-medium whitespace-nowrap">{g.label}</span>
              <span className="ml-auto text-caption text-muted-foreground whitespace-nowrap">
                {g.hint}
              </span>
            </button>
          )
        })}
      </PopoverContent>
    </Popover>
  )

  // Stepping is a paused-only action, and that is the whole rule. It used to be barred in
  // real-time as well, which would now bar it at one arbitrary pace out of five.
  const stepDisabled = playing
  const stepBackLabel = 'Step back one bar'
  const stepForwardLabel = 'Step forward one bar'
  const stepBackTitle = stepBackLabel
  const stepForwardTitle = stepForwardLabel

  // ── The phone band ──────────────────────────────────────────────────────────────────────
  // Its OWN ROW in the chart column, between the chart and the trades pane — not an overlay
  // on the chart (see the render site in WorkbenchWalkthrough). Square corners, no shadow,
  // nothing to drag. Everything fits at 375px, so there is no scroller and therefore no
  // control that can be swiped out of reach.
  if (isPhone) {
    return (
      <div
        ref={bandRef}
        data-testid="replay-band"
        className="relative w-full shrink-0 bg-popover select-none"
      >
        {/*
          THE BAND'S TOP EDGE IS THE PROGRESS BAR. No separate track and no percentage
          readout: a hairline rule across the top edge with a rule of TWICE its thickness
          advancing along it, the way a mobile page's own progress bar reads. Tailwind has no
          half-pixel, so the 0.5px/1px design is built as 1px/2px (`h-px` / `h-0.5`) — the
          DOUBLING is what makes the indicator read against its track, and that is what is
          preserved. In the app's own foreground tone rather than a red: green/red is
          reserved for financial data here, and a red rule would sit inches under red candles
          that mean something specific.

          THE BROWSER OWNS THE GESTURE. A hand-rolled pointer drag cannot be made to work
          here: iOS Safari raises `pointercancel` the moment it decides a horizontal drag
          belongs to the page, and every hand-rolled version reads that as "the drag ended" —
          so the press lands, the thumb appears, Safari takes the gesture, the drag tears
          itself down and nothing moves. `touch-action: none` does not prevent it once the
          touch has started; the only documented workaround is a NON-PASSIVE `touchstart`
          preventDefault, which React's own touch props cannot do. So the gesture goes to a
          native `<input type="range">` — which is what this component already uses on
          desktop. The input is transparent and sits over our own painted rules, so the look
          is kept exactly while the browser does the dragging underneath, and keyboard plus
          screen-reader support arrive free. It also means a press SEEKS as well as a drag,
          which is native range behaviour and an improvement.
        */}
        <div className="relative h-2 w-full">
          <div className="absolute inset-x-0 top-0 h-px bg-foreground/30" />
          <div className="absolute left-0 top-0 h-0.5 bg-foreground" style={{ width: `${pct}%` }} />
          {/* Invisible but live: `opacity-0` still receives every press. It also carries
              `peer`, which is what lets the thumb below grow while it is held without any JS
              state at all — one less thing that can be out of step with the real gesture. */}
          <input
            type="range"
            min={0}
            max={scrubMax}
            value={value}
            onChange={(e) => scrubTo(Number(e.target.value))}
            title="Scrub through the walkthrough"
            aria-label="Scrub through the walkthrough"
            className="peer absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent opacity-0 touch-none"
          />
          {/* The thumb at the leading edge — always visible, small at rest, expanding while
              held. `pointer-events-none` so it never intercepts the press meant for the
              input beneath it. */}
          <div
            className="pointer-events-none absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground transition-[height,width] peer-active:h-3.5 peer-active:w-3.5"
            style={{ left: `${pct}%`, top: '1px' }}
          />
        </div>
        {/* Even air above and below the icons: 8px of press strip above, a matching 8px
            beneath the row. */}
        <div className="mb-2 flex h-9 items-center justify-between px-1">
          <ToolbarButton
            phone
            compact
            title={stepBackTitle}
            label={stepBackLabel}
            disabled={stepDisabled}
            onClick={onStepBack}
          >
            <StepBack size={15} strokeWidth={1.75} />
          </ToolbarButton>
          <ToolbarButton
            phone
            compact
            title={playing ? 'Pause' : 'Play'}
            onClick={onTogglePlay}
            className="text-foreground"
          >
            {playing ? <Pause size={15} strokeWidth={2} /> : <Play size={15} strokeWidth={2} />}
          </ToolbarButton>
          <ToolbarButton
            phone
            compact
            title={stepForwardTitle}
            label={stepForwardLabel}
            disabled={stepDisabled}
            onClick={onStepForward}
          >
            <StepForward size={15} strokeWidth={1.75} />
          </ToolbarButton>
          {paceControl(true)}
          {detailControl(true)}
        </div>
      </div>
    )
  }

  return (
    // Two elements on purpose. The outer one owns the anchoring — docked at the BOTTOM of the
    // chart, just above the price/time axis, TradingView-style, so the transport is where the
    // eye expects a player. The inner one carries the drag offset, so the two never fight
    // over the transform property (and useFloatingBarDrag's clamp measures the outer one's
    // rect as the untransformed rest position).
    <div
      className={cn('absolute z-20 bottom-8 left-1/2 -translate-x-1/2', className)}
    >
      <div
        ref={barRef}
        data-testid="replay-bar"
        style={drag.dx || drag.dy ? { transform: `translate(${drag.dx}px, ${drag.dy}px)` } : undefined}
        // Uneven padding, and it has to be: the leading edge is a grip whose dots carry their
        // own inset, while the trailing edge is now bare text — the percentage readout, which
        // Exit used to sit outside of. 6px left it reading as though it had been cropped.
        className="flex items-center gap-1 h-9 pl-1 pr-3 rounded-lg bg-popover shadow-popover select-none
                   border border-foreground/30"
      >
        {onDragChange && (
          // Double-click to re-centre. Wrapped rather than folded into Grip so the two other
          // toolbars that share it are untouched.
          <span onDoubleClick={onDragReset} className="flex items-center">
            <Grip drag={drag} setDrag={onDragChange} />
          </span>
        )}

        {/* The controls row, kept as its own element so the grip stays outside it and the
            transport keeps its own min-width behaviour. Standard player order: step-back ·
            play · step-forward, the shape every media player taught. */}
        <div className="flex items-center min-w-0 gap-1 [&>*]:shrink-0">
          <IconButton
            onClick={onStepBack}
            title={stepBackTitle}
            label={stepBackLabel}
            disabled={stepDisabled}
          >
            <StepBack size={13} strokeWidth={1.75} />
          </IconButton>
          <button
            type="button"
            onClick={onTogglePlay}
            title={playing ? 'Pause' : 'Play'}
            aria-label={playing ? 'Pause' : 'Play'}
            className="inline-flex items-center justify-center h-6 w-6 rounded-md text-foreground
                       hover:bg-muted/60 transition-colors cursor-pointer"
          >
            {playing ? <Pause size={13} strokeWidth={2} /> : <Play size={13} strokeWidth={2} />}
          </button>
          <IconButton
            onClick={onStepForward}
            title={stepForwardTitle}
            label={stepForwardLabel}
            disabled={stepDisabled}
          >
            <StepForward size={13} strokeWidth={1.75} />
          </IconButton>

          {paceControl(false)}
          {detailControl(false)}

          <div className="w-px h-4 bg-border mx-1" />

          <input
            type="range"
            min={0}
            max={scrubMax}
            value={value}
            onChange={(e) => scrubTo(Number(e.target.value))}
            title="Scrub through the walkthrough"
            aria-label="Scrub through the walkthrough"
            className="h-1 accent-primary cursor-pointer w-40"
          />
          <span className="text-caption tabular-nums text-muted-foreground w-9 text-right">
            {Math.round(pct)}%
          </span>
        </div>
      </div>
    </div>
  )
}
